/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { TPreview } from "@tivocorp/data-domain-lib";

import { Locale } from "../../locale/Locale";
import { LocaleKeys } from "../../locale/LocaleKeys";

/**
 * Ported from kaveri-web-app `src/core/videoplayer/utils/AiringPreviewUtils.ts`.
 *
 * Instance-based utility that extracts display-ready metadata from airing
 * offers and offer previews. Constructed with the current airing data and
 * preview data, then queried by the UI layer for titles, descriptions,
 * season/episode info, ratings, etc.
 *
 * Until Phase 0-A.1 / 0-A.7 completes, `AiringOffer` and preview types
 * are represented as `Record<string, unknown>`. When the domain-lib
 * packages land these should be replaced with the strongly-typed versions.
 *
 * No optional chaining (`?.`) is used for Smart TV compatibility.
 */

// TODO: Replace with import from @tivocorp/data-domain-lib/client-code/airing when Phase 0-A.1 lands
type TAiringOffer = Record<string, unknown>;

const LEFT_QUOTE = "\u2018";
const RIGHT_QUOTE = "\u2019";
const COMMA = ",";

/**
 * Safely read a nested property from a loose record.
 */
const readProp = (
  source: Record<string, unknown> | undefined,
  key: string
): unknown => {
  if (source === undefined || source === null) {
    return undefined;
  }
  return source[key];
};

/**
 * Safely read a string from a loose record.
 */
const readString = (
  source: Record<string, unknown> | undefined,
  key: string
): string | undefined => {
  const value = readProp(source, key);
  return typeof value === "string" ? value : undefined;
};

/**
 * Safely read a number from a loose record.
 */
const readNumber = (
  source: Record<string, unknown> | undefined,
  key: string
): number | undefined => {
  const value = readProp(source, key);
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  return undefined;
};

/**
 * Safely read a boolean from a loose record.
 */
const readBoolean = (
  source: Record<string, unknown> | undefined,
  key: string
): boolean | undefined => {
  const value = readProp(source, key);
  return typeof value === "boolean" ? value : undefined;
};

/**
 * Safely read a nested object from a loose record.
 */
const readRecord = (
  source: Record<string, unknown> | undefined,
  key: string
): Record<string, unknown> | undefined => {
  const value = readProp(source, key);
  if (value !== null && value !== undefined && typeof value === "object") {
    return value as Record<string, unknown>;
  }
  return undefined;
};

/**
 * Generate season/episode text from season and episode numbers.
 */
const generateSeasonEpisodeText = (
  seasonNumber: number,
  episodeNumber: number
): string => {
  return `S${seasonNumber} E${episodeNumber}`;
};

/**
 * Add seconds to a date and return the new date.
 */
const addSecondsToDateTime = (date: Date, seconds: number): Date => {
  return new Date(date.getTime() + seconds * 1000);
};

export class AiringPreviewUtils {
  private airingData: TAiringOffer[] | undefined;

  private channelTitleText: string = "";

  private channelTitleTextNext: string = "";

  private checkAiringDataLength: number = 0;

  private parentalControlsRatingSeasonEpisodeSeperator: boolean = false;

  private parentalControlsRatingStarRatingSeperator: boolean = false;

  private previewData: TPreview | undefined;

  private timeParentalControlsRatingSeperator: boolean = false;

  public constructor(airingOffer?: TAiringOffer[], offerPreview?: TPreview) {
    this.airingData = airingOffer;
    this.previewData = offerPreview;
    this.checkAiringDataLength =
      this.airingData !== undefined ? this.airingData.length : 0;
    this._evaluateMetadataSeperators();
    this._formatChannelTitle();
    this._formatChannelTitleNext();
  }

  private _evaluateMetadataSeperators = (): void => {
    const isInternalRatingLoaded = this.isInternalRatingLoaded();
    const getTime = this.getDuration();

    this.timeParentalControlsRatingSeperator = !!(
      getTime && isInternalRatingLoaded
    );
    this.parentalControlsRatingSeasonEpisodeSeperator = !!(
      (getTime || isInternalRatingLoaded) &&
      this.getSeasonEpisode() !== null
    );
    this.parentalControlsRatingStarRatingSeperator = !!(
      (getTime || isInternalRatingLoaded) &&
      this.getStarRating() !== 0
    );
  };

  private _formatChannelTitle = (): void => {
    const programType = this.getProgramType();
    if (
      (programType === "movie" || programType === "moviePreview") &&
      this.getMovieYear() &&
      !this.shouldHideMetadata()
    ) {
      this.channelTitleText = `${this.getChannelTitle()} (${this.getMovieYear()})`;
    } else {
      this.channelTitleText = this.getChannelTitle();
    }
  };

  private _formatChannelTitleNext = (): void => {
    if (
      this.getProgramTypeNext() === "movie" &&
      !this.hasAdultNext() &&
      this.getMovieYearNext()
    ) {
      this.channelTitleTextNext = `${this.getChannelTitleNext()} (${this.getMovieYearNext()})`;
    } else {
      this.channelTitleTextNext = this.getChannelTitleNext();
    }
  };

  public generateCast = (): string | undefined => {
    const castText = readString(this.previewData, "creditString");
    const castLength = 125;
    if (castText && castText.length > castLength) {
      const castString = castText.slice(0, castLength);
      if (castString[castLength - 1] === COMMA) {
        return castString.slice(0, castLength - 1);
      }
      const castList = castString.split(COMMA);
      if (Array.isArray(castList)) {
        castList.pop();
        return castList.join(COMMA);
      }
    }
    return castText;
  };

  public generateGenre(): string {
    const genreLength =
      this.getProgramType() === "movie" || this.getFirstAiredDate() === ""
        ? 27
        : 23;

    const firstAiring =
      this.airingData !== undefined && this.airingData.length > 0
        ? this.airingData[0]
        : undefined;
    const logging = readRecord(firstAiring, "logging");

    const genreText =
      readString(logging, "genre") ||
      readString(this.previewData, "categoryLabel") ||
      "";

    if (genreText.length > genreLength) {
      let genreString = genreText.slice(0, genreLength);
      if (genreString.endsWith(COMMA)) {
        genreString = genreString.slice(0, -1);
      }
      const genreList = genreString.split(COMMA);
      if (Array.isArray(genreList)) {
        genreList.pop();
        return genreList.join(COMMA);
      }
    }

    return genreText;
  }

  public getSeasonEpisodeTitle = (): Array<string> => {
    const title: Array<string> = [];
    const seasonEpisode = this.getSeasonEpisode();
    const episodeTitle = this.getEpisodeTitle();
    title.push(`${this.getChannelTitleText()}`);
    if (seasonEpisode) {
      let seasonDetails = seasonEpisode;
      if (episodeTitle) {
        seasonDetails += ` ${LEFT_QUOTE}${episodeTitle}${RIGHT_QUOTE}`;
      }
      title.push(seasonDetails);
    } else {
      if (episodeTitle) {
        title.push(` ${LEFT_QUOTE}${episodeTitle}${RIGHT_QUOTE}`);
      }
    }
    return title;
  };

  public getCast = (): string | null | undefined => {
    if (this.shouldHideMetadata()) return null;
    return this.generateCast();
  };

  public getChannelTitle = (): string => {
    if (this.shouldHideMetadata()) {
      return Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN);
    }
    const firstAiring =
      this.airingData !== undefined && this.airingData.length > 0
        ? this.airingData[0]
        : undefined;
    const banner = readRecord(firstAiring, "banner");
    const bannerTitle = readString(banner, "title");
    if (bannerTitle) return bannerTitle;

    const previewTitle = readString(this.previewData, "title");
    if (previewTitle) return previewTitle;

    return Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE);
  };

  public getChannelTitleNext = (): string => {
    if (this.shouldHideMetadata()) {
      return Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN);
    }
    const secondAiring =
      this.airingData !== undefined && this.airingData.length > 1
        ? this.airingData[1]
        : undefined;
    const banner = readRecord(secondAiring, "banner");
    const bannerTitle = readString(banner, "title");
    if (bannerTitle) return bannerTitle;

    const previewTitle = readString(this.previewData, "title");
    if (previewTitle) return previewTitle;

    return Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE);
  };

  public getChannelTitleText = (): string => {
    return (
      this.channelTitleText || Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE)
    );
  };

  public getChannelTitleTextNext = (): string => {
    return (
      this.channelTitleTextNext ||
      Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE)
    );
  };

  public getDescription = (): string => {
    const description = readString(this.previewData, "description");
    if (description) {
      return this.shouldHideMetadata()
        ? Locale.getString(LocaleKeys.GUIDE_DESCRIPTION_HIDDEN)
        : description;
    }
    return Locale.getString(LocaleKeys.DESCRIPTION_NOT_AVAILABLE);
  };

  public getDuration = (): number => {
    const firstAiring =
      this.airingData !== undefined && this.airingData.length > 0
        ? this.airingData[0]
        : undefined;
    const airingDuration = readNumber(firstAiring, "duration");
    if (airingDuration !== undefined) return airingDuration;

    const previewDuration = readNumber(this.previewData, "duration");
    if (previewDuration !== undefined) return previewDuration;

    return 0;
  };

  public getEndTime = (): Date => {
    if (this.getDuration() !== 0 && this.getStartTime() !== "") {
      return addSecondsToDateTime(
        new Date(this.getStartTime()),
        this.getDuration()
      );
    }
    const endTime = readString(this.previewData, "endTime");
    if (endTime) return new Date(endTime);

    return new Date("");
  };

  /**
   * Returns airing start time as epoch seconds, or 0 when unavailable.
   */
  public getStartTimeEpoch = (): number => {
    const startTime = this.getStartTime();
    if (startTime === "") return 0;
    const ms = new Date(startTime).getTime();
    if (isNaN(ms)) return 0;
    return Math.floor(ms / 1000);
  };

  /**
   * Returns airing end time as epoch seconds, or 0 when unavailable.
   */
  public getEndTimeEpoch = (): number => {
    const endTime = this.getEndTime();
    const ms = endTime.getTime();
    if (isNaN(ms)) return 0;
    return Math.floor(ms / 1000);
  };

  public getEpisodeTitle = (): string => {
    if (this.shouldHideMetadata()) {
      return Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN);
    }
    const firstAiring =
      this.airingData !== undefined && this.airingData.length > 0
        ? this.airingData[0]
        : undefined;
    const banner = readRecord(firstAiring, "banner");
    const subtitle = readString(banner, "subtitle");
    if (subtitle) return subtitle;

    const episodeTitle = readString(this.previewData, "episodeTitle");
    if (episodeTitle) return episodeTitle;

    return "";
  };

  public getFirstAiredDate = (): string | null => {
    if (this.getProgramType() === "movie") return null;

    const firstAiring =
      this.airingData !== undefined && this.airingData.length > 0
        ? this.airingData[0]
        : undefined;
    const logging = readRecord(firstAiring, "logging");
    const originalAirDate = readString(logging, "originalAirDate");

    const firstAiredDate = readString(this.previewData, "firstAiredDate");

    const airingDate = originalAirDate || firstAiredDate;
    if (!airingDate) return null;

    const airingEpoch = new Date(airingDate).getTime();
    const nowEpoch = new Date().getTime();
    const prefixText =
      airingEpoch >= nowEpoch
        ? Locale.getString(LocaleKeys.FIRST_AIRS)
        : Locale.getString(LocaleKeys.FIRST_AIRED);

    const date = new Date(airingDate);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear() % 100;
    return `${prefixText} ${month}/${day}/${year}`;
  };

  public getGenre = (): string | false => {
    if (!this.airingData || this.checkAiringDataLength === 0) {
      return false;
    }
    if (this.hasGenre()) {
      return this.generateGenre();
    }
    return false;
  };

  public getIsAvailableInSocu = (): boolean => {
    if (!this.previewData) {
      return false;
    }
    return !!readBoolean(this.previewData, "isAvailableInSocu");
  };

  public getMovieYear = (): number => {
    const firstAiring =
      this.airingData !== undefined && this.airingData.length > 0
        ? this.airingData[0]
        : undefined;
    const banner = readRecord(firstAiring, "banner");
    const bannerYear = readNumber(banner, "movieYear");
    if (bannerYear !== undefined) return bannerYear;

    const previewYear = readNumber(this.previewData, "movieYear");
    if (previewYear !== undefined) return previewYear;

    return 0;
  };

  public getMovieYearNext = (): number => {
    const secondAiring =
      this.airingData !== undefined && this.airingData.length > 1
        ? this.airingData[1]
        : undefined;
    const banner = readRecord(secondAiring, "banner");
    const bannerYear = readNumber(banner, "movieYear");
    if (bannerYear !== undefined) return bannerYear;

    return 0;
  };

  public getParentalControlsRating = (): ReadonlyArray<
    Record<string, unknown>
  > | null => {
    const firstAiring =
      this.airingData !== undefined && this.airingData.length > 0
        ? this.airingData[0]
        : undefined;
    const parentalControls = readRecord(firstAiring, "parentalControls");
    const rating = readProp(parentalControls, "rating");
    if (Array.isArray(rating)) return rating as Record<string, unknown>[];

    const internalRatingList = readProp(this.previewData, "internalRatingList");
    if (Array.isArray(internalRatingList)) {
      return internalRatingList as Record<string, unknown>[];
    }

    return null;
  };

  public getParentalControlsRatingSeasonEpisodeSeperator(): boolean {
    return this.parentalControlsRatingSeasonEpisodeSeperator;
  }

  public getParentalControlsRatingStarRatingSeperator(): boolean {
    return this.parentalControlsRatingStarRatingSeperator;
  }

  public getProgramType = (): string => {
    const firstAiring =
      this.airingData !== undefined && this.airingData.length > 0
        ? this.airingData[0]
        : undefined;
    const banner = readRecord(firstAiring, "banner");
    const bannerType = readString(banner, "programType");
    if (bannerType) return bannerType;

    const previewType = readString(this.previewData, "previewType");
    if (previewType) return previewType;

    return "";
  };

  public getProgramTypeNext = (): string => {
    const secondAiring =
      this.airingData !== undefined && this.airingData.length > 1
        ? this.airingData[1]
        : undefined;
    const banner = readRecord(secondAiring, "banner");
    return readString(banner, "programType") || "";
  };

  public getSeasonEpisode = (): string | null => {
    const firstAiring =
      this.airingData !== undefined && this.airingData.length > 0
        ? this.airingData[0]
        : undefined;
    const banner = readRecord(firstAiring, "banner");
    const seasonNumber = readNumber(banner, "seasonNumber");
    const episodeNumber = readNumber(banner, "episodeNumber");
    if (seasonNumber !== undefined && episodeNumber !== undefined) {
      return generateSeasonEpisodeText(seasonNumber, episodeNumber);
    }

    const previewSeason = readNumber(this.previewData, "seasonNumber");
    const previewEpisode = readNumber(this.previewData, "episodeNumber");
    if (previewSeason !== undefined && previewEpisode !== undefined) {
      return generateSeasonEpisodeText(previewSeason, previewEpisode);
    }

    return null;
  };

  /**
   * Raw season number, or `undefined` when not an episodic airing.
   */
  public getSeasonNumber = (): number | undefined => {
    const firstAiring =
      this.airingData !== undefined && this.airingData.length > 0
        ? this.airingData[0]
        : undefined;
    const banner = readRecord(firstAiring, "banner");
    const bannerSeason = readNumber(banner, "seasonNumber");
    if (bannerSeason !== undefined) return bannerSeason;

    return readNumber(this.previewData, "seasonNumber");
  };

  /**
   * Raw episode number, or `undefined` when not an episodic airing.
   */
  public getEpisodeNumber = (): number | undefined => {
    const firstAiring =
      this.airingData !== undefined && this.airingData.length > 0
        ? this.airingData[0]
        : undefined;
    const banner = readRecord(firstAiring, "banner");
    const bannerEpisode = readNumber(banner, "episodeNumber");
    if (bannerEpisode !== undefined) return bannerEpisode;

    return readNumber(this.previewData, "episodeNumber");
  };

  /**
   * Raw ISO original air date — distinct from getFirstAiredDate()'s
   * localized/formatted display string.
   */
  public getOriginalAirDate = (): string | undefined => {
    const firstAiring =
      this.airingData !== undefined && this.airingData.length > 0
        ? this.airingData[0]
        : undefined;
    const logging = readRecord(firstAiring, "logging");
    const originalAirDate = readString(logging, "originalAirDate");
    if (originalAirDate) return originalAirDate;

    return readString(this.previewData, "firstAiredDate");
  };

  public getShowAttributes = (
    currentPlaybackResolution?: number
  ): string | false => {
    const showAttributes: string[] = [];
    if (this.getVideoResolution().length) {
      showAttributes.push(this.getVideoResolution().toLocaleUpperCase());
    }
    if (this.hasCC()) {
      showAttributes.push("CC");
    }
    if (this.hasSAP()) {
      showAttributes.push("SAP");
    }
    if (currentPlaybackResolution) {
      showAttributes.push(`${currentPlaybackResolution}p`);
    }
    if (showAttributes.length === 0) {
      return false;
    }
    return showAttributes.join(", ");
  };

  public getStarRating = (): number => {
    const firstAiring =
      this.airingData !== undefined && this.airingData.length > 0
        ? this.airingData[0]
        : undefined;
    const banner = readRecord(firstAiring, "banner");
    const bannerRating = readNumber(banner, "starRating");
    if (bannerRating !== undefined) return bannerRating;

    const previewRating = readNumber(this.previewData, "starRating");
    if (previewRating !== undefined) return previewRating;

    return 0;
  };

  public getStartTime = (): string => {
    const firstAiring =
      this.airingData !== undefined && this.airingData.length > 0
        ? this.airingData[0]
        : undefined;
    const airingStart = readString(firstAiring, "startTime");
    if (airingStart) return airingStart;

    const previewStart = readString(this.previewData, "startTime");
    if (previewStart) return previewStart;

    return "";
  };

  public getTimeParentalControlsRatingSeperator(): boolean {
    return this.timeParentalControlsRatingSeperator;
  }

  public getVideoResolution = (): string => {
    const firstAiring =
      this.airingData !== undefined && this.airingData.length > 0
        ? this.airingData[0]
        : undefined;
    const logging = readRecord(firstAiring, "logging");
    const airingResolution = readString(logging, "videoResolution");
    if (airingResolution !== undefined) return airingResolution;

    const previewResolution = readString(this.previewData, "videoResolution");
    if (previewResolution !== undefined) return previewResolution;

    return "";
  };

  public shouldHideMetadata = (): boolean => {
    const firstAiring =
      this.airingData !== undefined && this.checkAiringDataLength > 0
        ? this.airingData[0]
        : undefined;
    const parentalControls = readRecord(firstAiring, "parentalControls");
    const isAdultAiring = readBoolean(parentalControls, "isAdult");

    const isAdultPreview = readBoolean(this.previewData, "isAdult");

    // TODO: Call ServicesClientImpl.shouldHideMetadata() when available
    return isAdultAiring === true || isAdultPreview === true;
  };

  public hasAdultNext = (): boolean => {
    if (!this.airingData) {
      return false;
    }
    const secondAiring =
      this.airingData.length > 1 ? this.airingData[1] : undefined;
    const parentalControls = readRecord(secondAiring, "parentalControls");
    const isAdult = readBoolean(parentalControls, "isAdult");
    if (isAdult === true) return true;
    return false;
  };

  public hasCC = (): boolean => {
    return !!(this.previewData && readBoolean(this.previewData, "hasCc"));
  };

  public hasGenre = (): boolean => {
    if (!this.airingData) {
      return false;
    }
    const firstAiring =
      this.checkAiringDataLength > 0 ? this.airingData[0] : undefined;
    const logging = readRecord(firstAiring, "logging");
    const genre = readString(logging, "genre");
    return !!(
      this.checkAiringDataLength &&
      genre !== undefined &&
      genre !== ""
    );
  };

  public hasNew = (): boolean => {
    const firstAiring =
      this.airingData !== undefined && this.airingData.length > 0
        ? this.airingData[0]
        : undefined;
    const banner = readRecord(firstAiring, "banner");
    const isNewFromAiring = readBoolean(banner, "isNew");

    const isNewFromPreview = readBoolean(this.previewData, "isNew");

    return !!isNewFromAiring || !!isNewFromPreview;
  };

  public hasNewNext = (): boolean => {
    if (!this.airingData || this.checkAiringDataLength === 0) {
      return false;
    }
    const secondAiring =
      this.airingData.length > 1 ? this.airingData[1] : undefined;
    const banner = readRecord(secondAiring, "banner");
    return !!readBoolean(banner, "isNew");
  };

  public hasSAP = (): boolean => {
    return !!(this.previewData && readBoolean(this.previewData, "hasSap"));
  };

  public isInternalRatingLoaded = (): boolean => {
    const parentalControlsRating = this.getParentalControlsRating();
    return !!(parentalControlsRating && parentalControlsRating.length > 0);
  };
}
